
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { FlaskConical, Brain, Building2, Brush, Megaphone, Lightbulb } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const JessProfile = () => {
  const user = {
    name: "Jess Simm",
    role: "iSPAI Chief Marketing Officer",
    solutionBounties: 8,
    domains: [
      "Brand Strategy",
      "Marketing",
      "Creative Direction",
      "Technical Communication",
      "Community Building",
      "Digital Marketing",
      "Brand Development",
      "AI Integration",
      "Global Marketing"
    ],
    industries: [
      { name: "Biotech", icon: FlaskConical },
      { name: "Industrial AI", icon: Brain },
      { name: "Enterprise", icon: Building2 },
      { name: "Creative", icon: Brush },
      { name: "Marketing", icon: Megaphone },
      { name: "Innovation", icon: Lightbulb }
    ],
    confirmedSolutions: 32,
    confirmedKnowledgeBases: 64,
    tokenBalance: 200,
    bio: "Jess Simm is a brand and marketing expert and will serve as the Chief Marketing Officer for iSPAI. With over 13 years of experience in the biotech and industrial AI industries, she is a brand futurist and marketing utility knife. Jess is the founder of Simmetry Brand Studio and excels in bridging the gap between highly technical individuals and creative minds through her unique blend of creative problem-solving and practical analysis. Based in California and Australia, and originally from New Zealand, Jess has worked with large enterprises and startups alike, including Beyond Limits AI, where she was employee number 7. Her experience also includes roles at Diversify the Stage, Datch AI, SIVA Enterprises, and Genentech. Jess holds a BBA in Information Technology from Kingston International College in Australia. As the Chief Marketing Officer for iSPAI, Jess will help define, own, and manage the brand of iSPAI whilst ensuring we build communities around the intersection of AI-enabled Consumers and Creators worldwide."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/ab55eb61-47c2-43c7-8695-ea7c7b3b0b53.png"
                    imageAlt="Jess Simm"
                  >
                    <Avatar className="h-48 w-48 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/ab55eb61-47c2-43c7-8695-ea7c7b3b0b53.png" 
                        alt="Jess Simm"
                      />
                      <AvatarFallback>JS</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="bg-white text-black hover:bg-gray-100"
                        >
                          BIO
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <div className="space-y-2">
                          <h4 className="text-lg font-semibold">Jess Simm</h4>
                          <p className="text-sm text-gray-600">{user.bio}</p>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="bg-white text-black hover:bg-gray-100"
                    >
                      NW
                    </Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <p className="text-lg font-medium text-black mb-4">{user.role}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/jess"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.4} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default JessProfile;
